/**
 * A living light-stream connecting two energy nodes. When `active`, particles
 * flow along the path via animated stroke-dashoffset. Otherwise the path is
 * a dim latent line so the topology is still legible.
 */
export type EnergyChannelProps = {
  x1: number; y1: number;
  x2: number; y2: number;
  hue?: number;
  active?: boolean;
  defined?: boolean;
  label?: string;
};

export function EnergyChannel({ x1, y1, x2, y2, hue = 44, active = false, defined = false, label }: EnergyChannelProps) {
  const color = `hsl(${hue} 85% 60%)`;
  // Slight arc: perpendicular offset for organic feel
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len;
  const bow = Math.min(len * 0.08, 22);
  const px = mx + nx * bow;
  const py = my + ny * bow;
  const d = `M ${x1} ${y1} Q ${px} ${py} ${x2} ${y2}`;

  const baseOpacity = defined ? 0.55 : 0.18;
  const activeOpacity = active ? 1 : baseOpacity;

  return (
    <g aria-label={label}>
      {/* Latent path */}
      <path d={d} fill="none" stroke={color} strokeOpacity={baseOpacity} strokeWidth={1} />
      {/* Flowing overlay */}
      {(active || defined) && (
        <path
          d={d}
          fill="none"
          stroke={color}
          strokeOpacity={activeOpacity}
          strokeWidth={active ? 2.2 : 1.4}
          strokeLinecap="round"
          strokeDasharray="6 10"
          className="motion-safe:animate-[energy-flow_2.4s_linear_infinite]"
        />
      )}
    </g>
  );
}
